<template>
  <div
    class="relative flex flex-col items-center justify-center w-full min-h-[200px] rounded-xl border-2 border-dashed transition-all duration-200 cursor-pointer"
    :class="isDragOver ? 'border-primary bg-primary-muted/50' : 'border-border hover:border-primary/50 hover:bg-border-subtle/50'"
    role="button"
    tabindex="0"
    :aria-label="ariaLabel"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <input ref="fileInput" type="file" class="sr-only" :accept="accept" :multiple="multiple" @change="handleFileSelect" />

    <template v-if="!uploadedFile">
      <div class="flex flex-col items-center gap-3 p-8 text-center">
        <div class="flex items-center justify-center w-12 h-12 rounded-xl bg-primary-muted text-primary">
          <AppIcon name="Upload" :size="24" />
        </div>
        <div>
          <p class="text-sm font-medium text-foreground">Drop your file here</p>
          <p class="text-caption mt-1">or click to browse</p>
        </div>
        <p v-if="hint" class="text-xs text-muted">{{ hint }}</p>
      </div>
    </template>

    <template v-else>
      <div class="flex flex-col items-center gap-4 p-6 w-full" @click.stop>
        <img
          v-if="isImage(uploadedFile)"
          :src="previewUrl"
          :alt="uploadedFile.name"
          class="max-h-32 rounded-lg object-contain"
        />
        <div v-else class="flex items-center gap-3">
          <AppIcon name="FileArchive" :size="32" class="text-primary" />
          <span class="text-sm font-medium truncate max-w-[200px]">{{ uploadedFile.name }}</span>
        </div>
        <BaseButton variant="secondary" size="sm" @click.stop="reset">Remove file</BaseButton>
      </div>
    </template>
  </div>
</template>

<script>
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseButton from '@/components/ui/BaseButton.vue'

export default {
  name: 'FileDropzone',
  components: { AppIcon, BaseButton },
  props: {
    multiple: { type: Boolean, default: false },
    accept: { type: String, default: '*' },
    hint: String,
    ariaLabel: { type: String, default: 'Upload file' },
  },
  emits: ['file-selected'],
  data() {
    return {
      isDragOver: false,
      uploadedFile: null,
      previewUrl: null,
    }
  },
  methods: {
    handleDragOver() {
      this.isDragOver = true
    },
    handleDragLeave() {
      this.isDragOver = false
    },
    handleDrop(event) {
      this.isDragOver = false
      const file = event.dataTransfer.files[0]
      if (file) this.processFile(file)
    },
    handleClick() {
      if (!this.uploadedFile) this.$refs.fileInput.click()
    },
    handleFileSelect() {
      const file = this.$refs.fileInput.files[0]
      if (file) this.processFile(file)
      this.$refs.fileInput.value = null
    },
    processFile(file) {
      this.uploadedFile = file
      if (this.isImage(file)) {
        this.previewUrl = URL.createObjectURL(file)
      }
      this.$emit('file-selected', file)
    },
    isImage(file) {
      return file?.type?.startsWith('image/')
    },
    reset() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.uploadedFile = null
      this.previewUrl = null
      this.$emit('file-selected', null)
    },
  },
  beforeUnmount() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
  },
}
</script>
